<template>
  <div class="card">
    <div class="card-body">

      <!-- Payment success -->
      <div v-if="payment_success">
        <div class="col">
          <div class="row pb-3">
            <div class="col text-center">
              <h5>Thank you!</h5>
              <p class="mb-2 px-4">Your payment was successful</p>
              <p class="mb-0">Please check your email for confirmation</p>
            </div>
          </div>
          <div class="pt-1 pb-3 text-center">
            <a :href="'/projects/view/'+project.id+'/0'" class="btn btn-success">View project</a>
          </div>
        </div>
      </div>

      <!-- Make payment -->
      <div v-else class="row d-flex justify-content-center">
        <div class="col-md-6 pt-2">

          <div v-if="payment_response" class="alert alert-danger text-center">{{ payment_response }}</div>

          <!-- Part payment balance and card -->
          <div v-if="user.balance > 0 && user.balance < project.quote">
            <div class="alert alert-secondary text-center">
              {{ formattedPrice(user.balance) }} will be paid from your balance
              <br>You can pay the remaining {{ formattedPrice(project.quote - user.balance) }} with your card
            </div>
          </div>

          <!-- Payment from balance -->
          <div class="text-center" v-if="user.balance >= project.quote">
            <a v-if="loading" href="#/" class="btn btn-success mt-3 mb-3"><i class="fas fa-spinner fa-spin"></i></a>
            <a v-else href="#/" @click.prevent="payWithBalance" class="btn btn-success mt-3 mb-3">Pay {{ formattedPrice(project.quote) }} from balance</a>
          </div>

          <!-- Card payment -->
          <div v-else-if="cards == 0">
            <div class="text-center">
              <div v-if="publishableKey">
                <div class="text-center logos">
                  <img src="/images/visa_mastercard.svg" width="90">
                </div>
                <div id="stripe-card-element" class="form-control"></div>
                <div v-if="cardError" class="alert alert-danger mt-2 text-center">{{ cardError }}</div>
                <a v-if="loading" href="#/" class="btn btn-success mt-3 mb-3"><i class="fas fa-spinner fa-spin"></i></a>
                <a v-else href="#/" @click.prevent="submit" class="btn btn-success mt-3 mb-3">Pay {{ formattedPrice(project.quote - user.balance) }}</a>
              </div>
            </div>
          </div>

          <!-- Pay with saved card -->
          <div v-else class="text-center">
            <div class="alert alert-secondary text-center">You have a <a href="/billing" class="underline" title="Manage cards">saved card</a></div>
            <a v-if="loading" href="#/" class="btn btn-success mt-2 mb-3"><i class="fas fa-spinner fa-spin"></i></a>
            <a v-else @click.prevent="makePaymentNoToken" class="btn btn-success mt-2 mb-3">Pay {{ formattedPrice(project.quote - user.balance) }}</a>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
  project: Object,
  user: Object,
  cards: Number,
});

const token = ref(null);
const publishableKey = ref(null);
const payment_response = ref(null);
const payment_success = ref(false);
const loading = ref(false);
const cardError = ref(null);

let stripeInstance = null;
let cardElement = null;

function formattedPrice(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

async function getStripeKey() {
  try {
    const data = await $fetch('/subscriptions/stripe-key');
    publishableKey.value = data;
  } catch (error) {
    console.error('Failed to fetch Stripe key:', error);
  }
}

async function mountStripeElement() {
  if (!publishableKey.value || props.cards != 0) return;

  const { loadStripe } = await import('@stripe/stripe-js');
  stripeInstance = await loadStripe(publishableKey.value);

  if (!stripeInstance) return;

  const elements = stripeInstance.elements();
  cardElement = elements.create('card', {
    hidePostalCode: true,
    classes: {
      base: 'form-control',
      focus: 'form-control',
      invalid: 'stripe_invalid',
      complete: 'form-control',
      empty: 'stripe_empty',
    },
  });

  cardElement.mount('#stripe-card-element');

  cardElement.on('change', (event) => {
    cardError.value = event.error ? event.error.message : null;
  });
}

async function submit() {
  if (!stripeInstance || !cardElement) return;

  loading.value = true;
  payment_response.value = null;

  const { error, token: stripeToken } = await stripeInstance.createToken(cardElement);

  if (error) {
    cardError.value = error.message;
    loading.value = false;
    return;
  }

  token.value = stripeToken;
  await makePayment();
}

async function makePayment() {
  payment_response.value = null;
  loading.value = true;

  try {
    const response = await $fetch('/projects/payment', {
      method: 'POST',
      body: {
        stripeToken: token.value.id,
        project_id: props.project.id,
      },
    });

    if (response.success) {
      payment_success.value = true;
    } else {
      payment_response.value = response.message;
    }
  } catch (error) {
    console.error('Payment error:', error);
    payment_response.value = 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function makePaymentNoToken() {
  payment_response.value = null;
  loading.value = true;

  try {
    const response = await $fetch('/projects/payment', {
      method: 'POST',
      body: {
        stripeToken: null,
        project_id: props.project.id,
      },
    });

    if (response.success) {
      payment_success.value = true;
    } else {
      payment_response.value = response.message;
    }
  } catch (error) {
    console.error('Payment error:', error);
    payment_response.value = 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function payWithBalance() {
  payment_response.value = null;
  loading.value = true;

  try {
    const response = await $fetch('/projects/payment-balance', {
      method: 'POST',
      body: {
        project_id: props.project.id,
      },
    });

    if (response.success) {
      payment_success.value = true;
    } else {
      payment_response.value = response.message;
    }
  } catch (error) {
    console.error('Payment error:', error);
    payment_response.value = 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await getStripeKey();
  await mountStripeElement();
});
</script>

<style scoped>
.logos {
  background-color: #fff;
  padding: 0.4rem 2rem;
  border-radius: 5px;
  width: fit-content;
  margin: 0 auto 1rem auto;
}
.underline {
  text-decoration: underline;
}
.form-control {
  border-radius: 50px !important;
}
.form-control:focus {
  box-shadow: none !important;
  outline: none !important;
}
.StripeElement--focus {
  box-shadow: none !important;
  outline: none !important;
}
</style>
